import { useState } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Icon,
  IconButton,
  InputAdornment,
  SnackbarContent,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Email,
  Lock,
  FilterVintage,
  AccountCircle,
  Visibility,
  VisibilityOff,
  Close,
  Info,
  PermIdentity,
} from '@mui/icons-material';
import { useLogin } from 'Contexts/AuthProvider/AuthProvider';
import InformationDialog from 'Dialogs/Information/Information';
import { useDialog } from 'Contexts/Modals/ModalProvider';
import './styles.css';
import { ipayNet, newServer } from 'constants/constants';
import { useNavigate } from 'react-router-dom';
import { HomeConfig } from 'Config/home.config';
import { useLocation } from 'react-router-dom';
import HttpsIcon from '@mui/icons-material/Https';
import LockIcon from '@material-ui/icons/Lock';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const loginWith2FA = true;

const AuthForm = ({ isModal }: any) => {
  const { setUser } = useLogin();
  const { openDialog } = useDialog();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [_2fa, set2fa] = useState();
  const [_barNo, setBarNo] = useState('');
  const [visivility, setVisivility] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const location = useLocation();
  const isEmailValid = emailRegex.test(email);
  /**
   * Handlers Functions
   */
  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const handleForgotPasswordOpen = async () => {
    const subdomain = '';
    openDialog('reset-password', InformationDialog as any, {
      title: 'Reset Password',
      description:
        'Please enter the email address associated with your account and We will email you a link to reset your password.',
      icon: <HttpsIcon />,
      input: forgotPasswordEmail,
      setInput: (value: string) => setForgotPasswordEmail(value as any),
      inputPlaceholder: 'Enter email',
      cancelText: 'Cancel',
      onConfirm: (value: string) => handlePasswordReset(value, subdomain),
    });
  };

  const showModal = () => {
    openDialog('information', InformationDialog as any, {
      title: 'Login Completed',
      description: 'User created successfully',
      icon: <Info />,
    });
  };

  const showModal2fa = () => {
    openDialog('information', InformationDialog as any, {
      title: 'Verify 2FA (Two Factor Authentication)  Code',
      description:
        'To complete the login process, kindly verify the two-factor authentication code sent to your registered email or phone number. Please enter the code in the below field.',
      icon: <Info />,
      input: _2fa,
      setInput: set2fa,
      inputPlaceholder: '2FA code',
      cancelText: 'Cancel',
      onConfirm: validate2FA,
    });
  };

  const validate2FA = async (code: string) => {
    if (code) {
      try {
        const res = await fetch(`${newServer}login/2FA`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code: code,
            remember_me: false,
          }),
        });

        if (res.status === 200) {
          const data = await res.json();

          console.log('data', data);
          if (data?.token && data.token.error === undefined) {
            setUser({
              email,
              token: data.token,
              user_id: data?.user.id,
              role_id: data?.user.role_id,
              uuid: data?.user.uuid,
              bar_no: data?.user.bar_no,
            });
          } else {
            setAlertMessage(`Login failed. ${data?.token.info.message}.  Please try again.`);
            setSnackbarMessage('Login failed. Please try again.');
            setOpenSnackbar(true);
          }
          return;
        }

        setAlertMessage(await res.text());
      } catch (error) {
        console.log('validate2FA', error);
      }
    }
  };

  const getDomain = async () => {
    try {
      const response = await fetch(`${newServer}subdomain/get-domain-by-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': ipayNet.apiToken,
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.text();
      return data;
    } catch (error) {
      console.log('getDomain', error);
    }
  };

  const handleLogin = async () => {
    const subdomain = await getDomain();
    const user = { email, password, subdomain: subdomain };
    setLoading(true);
    setAlertMessage('');

    fetch(`${newServer}login/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': ipayNet.apiToken,
        /**
         * El backend resuelve el submerchant del tracking leyendo este header
         * (`trackerFunction` en tracker.middleware.js). Sin el, `subMerchantInfo`
         * recibe `undefined` y Knex lanza "Undefined binding(s)"; el error queda
         * silenciado y el evento se guarda sin `submerchant_id`.
         *
         * Se usa el subdominio resuelto por email y NO `hostname.split('.')[0]`
         * (como hace el portal Angular): la landing vive en el dominio raiz, asi
         * que el hostname daria "paymylawyer", que no es un subdominio valido.
         */
        ...(subdomain ? { submerchant: subdomain } : {}),
      },
      body: JSON.stringify(user),
    })
      .then(async (response) => {
        console.log(response);
        setLoading(false);
        if (response.status === 429) {
          // const { message } = await response.json();
          setSnackbarMessage(
            `Error: We've noticed some unusual activity coming from your IP address, so we've put a temporary pause on access as a safety measure. Don't worry, it's just a timeout! Give it a little while and try again. Need help sooner? Our support team is here for you.`
          );
          setOpenSnackbar(true);
          return;
        }
        if (response.status === 200) {
          if (loginWith2FA && (await response.text()) === 'OK') {
            showModal2fa();
            return;
          }

          const data = await response.json();
          console.log('data', data);
          if (data?.token && data.token.error === undefined) {
            /**
             * El backend responde `{ token, user }` y el id vive en `user.id`
             * (`login.controller.js`). Antes aqui se leia `data.user_id` y
             * `data.uuid`, que NO existen en esa respuesta: llegaban
             * `undefined`, y el `user_id` acababa inventado en `AuthProvider`.
             * La ruta 2FA de mas arriba ya lo leia bien; esto la iguala.
             */
            setUser({
              email,
              token: data.token,
              user_id: data?.user?.id,
              role_id: data?.user?.role_id,
              uuid: data?.user?.uuid,
              bar_no: data?.user?.bar_no,
            });
          } else {
            setAlertMessage(`Login failed. ${data?.token.info.message}.  Please try again.`);
            setSnackbarMessage('Login failed. Please try again.');
            setOpenSnackbar(true);
          }
        } else {
          const textMessage = await response.text();
          setAlertMessage(`Login failed. ${textMessage}.  Please try again.`);
          setSnackbarMessage('Login failed. Please try again.');
          setOpenSnackbar(true);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log('handleLogin', error);
        setAlertMessage(`Login failed.`);
        setSnackbarMessage('Login failed. Please try again.');
        setOpenSnackbar(true);
      });
  };

  const handleRegister = () => {
    setVisivility(true);
    if (_barNo !== '' && _barNo) {
      const user = {
        email,
        password,
        subDomain: 'subdominio1',
        subDomainName: 'Shelter 1',
        bar_no: _barNo,
      };
      setLoading(true);
      setAlertMessage('');

      fetch(`${newServer}users/simple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': ipayNet.apiToken,
        },
        body: JSON.stringify(user),
      })
        .then(async (response) => {
          setLoading(false);
          if (response.status === 200) {
            if (loginWith2FA && (await response.text()) === 'OK') {
              showModal2fa();
              return;
            }

            const data = await response.json();

            if (data?.token) {
              showModal();
              setUser({
                email,
                token: data.token,
                user_id: data?.user?.id,
                role_id: data?.user?.role_id,
                uuid: data?.user?.uuid,
                bar_no: data?.user?.bar_no,
              });
            }
          } else {
            const textMessage = await response.text();
            console.error('Registration failed', textMessage);
            setAlertMessage(`Registration failed. ${textMessage}.  Please try again.`);
            setSnackbarMessage('Registration failed. Please try again.');
            setOpenSnackbar(true);
          }
        })
        .catch((error) => {
          setLoading(false);
          console.log('handleRegister', error);
          setAlertMessage(`Registration failed. ${error}`);
          setSnackbarMessage('Registration failed. Please try again.');
          setOpenSnackbar(true);
        });
    } else {
      setSnackbarMessage('Please enter your Bar Card');
      setAlertMessage('Please enter your Bar Card');
      setOpenSnackbar(true);
    }
  };

  const handlePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handlePasswordReset = async (email: string, subdomain: any) => {
    try {
      const res = await fetch(`${newServer}password/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, subdomain }),
      });
      if (res.status === 200) {
        setSnackbarMessage('Password reset link sent to your email!');
        setOpenSnackbar(true);
      } else {
        setSnackbarMessage('Error resetting password. Please try again.');
        setOpenSnackbar(true);
      }
    } catch (error) {
      setSnackbarMessage('Error resetting password. Please try again.');
      setOpenSnackbar(true);
    }
  };

  return (
    /**
     * El color de la tarjeta, su radio y su borde ya vienen del tema (tokens).
     * Antes se forzaba `backgroundColor: '#1e293b'` aqui, que era otro navy
     * distinto del resto de la app y ademas ignoraba el cambio de tema.
     */
    <Card
      style={{
        padding: '20px',
        minWidth: '300px',
        maxWidth: '500px',
        margin: 'auto',
        height: '100%',
      }}
    >
      {alertMessage && <Alert severity='error'>{alertMessage}</Alert>}
      <CardContent>
        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <SnackbarContent
            message={<span>{snackbarMessage}</span>}
            action={
              <IconButton
                size='small'
                aria-label='close'
                color='inherit'
                onClick={handleSnackbarClose}
              >
                <Icon fontSize='small'>
                  <Close />
                </Icon>
              </IconButton>
            }
          />
        </Snackbar>

        {/*
          `color='#fff'` era un no-op: MUI espera claves de paleta
          (`textPrimary`, `textSecondary`...), no valores CSS, asi que no hacia
          nada y el color quedaba heredado. Ahora la jerarquia se expresa con
          claves reales de la paleta del tema.
        */}
        <Typography variant='h5' align='center' color='text.primary' gutterBottom>
          <Icon style={{ fontSize: '36px', marginTop: '10px' }}>
            <FilterVintage />
          </Icon>
          Registration Required
        </Typography>
        {/* El subtitulo baja a texto secundario: antes competia con el titulo. */}
        <Typography variant='body1' align='center' color='text.secondary' paragraph>
          * Log-In if already enrolled to {HomeConfig.merchatName}{' '}
        </Typography>

        {visivility && (
          <TextField
            label='Bar Card'
            variant='outlined'
            fullWidth
            margin='normal'
            required
            value={_barNo}
            onChange={(e) => setBarNo(e.target.value)}
            InputProps={{
              startAdornment: (
                <Icon style={{ marginRight: '8px' }}>
                  <PermIdentity />
                </Icon>
              ),
            }}
          />
        )}

        <TextField
          label='Email'
          variant='outlined'
          type='email'
          fullWidth
          required
          margin='normal'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          InputProps={{
            startAdornment: (
              <Icon style={{ marginRight: '8px' }}>
                <Email />
              </Icon>
            ),
          }}
        />

        <TextField
          label='Password'
          required
          fullWidth
          margin='normal'
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          InputProps={{
            startAdornment: (
              <Icon style={{ marginRight: '8px' }}>
                <Lock />
              </Icon>
            ),
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  onClick={handlePasswordVisibility}
                  edge='end'
                  aria-label={showPassword ? 'Ocultar contrasena' : 'Mostrar contrasena'}
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '40px' }}>
          {/*
            Se elimina el ternario `isEmailValid ? '#03a9f4' : '#161d28'`.

            Ese ternario tenia dos problemas: el color activo daba 2.63:1 con
            texto blanco (reprobase WCAG AA) y el deshabilitado era identico al
            fondo, asi que un boton apagado se veia igual que uno encendido. El
            estado deshabilitado ahora lo resuelve el tema, que si lo distingue.
          */}
          <Button
            variant='contained'
            onClick={handleLogin}
            disabled={!isEmailValid || loading}
            style={{ width: '500px', height: '50px' }}
          >
            {loading && <CircularProgress size={24} color='inherit' />}
            <LockIcon style={{ marginRight: '5px', marginBottom: '5px' }} />
            Login
          </Button>

          {location.pathname !== '/backoffice' && (
            <Button
              variant='contained'
              disabled={!isEmailValid || loading}
              onClick={handleRegister}
              style={{ width: '500px', height: '50px' }}
            >
              {loading && <CircularProgress size={24} color='inherit' />}
              <AccountCircle style={{ marginRight: '5px', marginBottom: '5px' }} />
              Enroll Now
            </Button>
          )}
        </div>

        <Typography
          variant='body2'
          align='center'
          color='text.secondary'
          style={{ cursor: 'pointer', marginTop: '15px' }}
          onClick={handleForgotPasswordOpen}
        >
          <a className='link'>Forgot Password</a>
        </Typography>

        <Typography
          variant='body2'
          align='center'
          color='text.secondary'
          style={{ marginTop: '30px' }}
        >
          By enrolling you accept our
          <a className='link' onClick={() => navigate('/terms')}>
            {' '}
            Terms and Conditions
          </a>{' '}
          and our{' '}
          <a className='link' onClick={() => navigate('/policies')}>
            Privacy Policy
          </a>
          .
        </Typography>
      </CardContent>
    </Card>
  );
};

export default AuthForm;
